import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import DateQuickPresets from '../../components/DateQuickPresets';

const MargemCentroPlano = () => {
  const now = new Date();
  const [carregando, setCarregando] = useState(false);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [filtros, setFiltros] = useState({
    data_inicio: format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'),
    data_fim: format(now, 'yyyy-MM-dd'),
    regime: 'competencia',
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas',
    top_n: 30,
  });
  const [dados, setDados] = useState({
    resumo: { receitas: 0, despesas: 0, resultado: 0, margem_pct: null },
    por_centro: [],
    por_plano: [],
  });

  const formatarValor = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatarPct = (value) => (value == null ? '-' : `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%`);

  const carregarFiltros = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias'),
      ]);
      setCentrosCustos(Array.isArray(centrosResponse.data) ? centrosResponse.data : []);
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      console.error('Erro ao carregar filtros de margem:', error);
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/margem-centro-plano', { params: filtros });
      const payload = response.data || {};
      setDados({
        resumo: payload.resumo || { receitas: 0, despesas: 0, resultado: 0, margem_pct: null },
        por_centro: Array.isArray(payload.por_centro) ? payload.por_centro : [],
        por_plano: Array.isArray(payload.por_plano) ? payload.por_plano : [],
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar margem por centro/plano');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarFiltros();
  }, []);

  useEffect(() => {
    carregarDados();
  }, [filtros]);

  const chartCentro = useMemo(
    () =>
      (dados.por_centro || []).slice(0, 12).map((item) => ({
        nome: `${item.centro_codigo} ${item.centro_descricao}`.trim().slice(0, 24),
        receitas: item.receitas,
        despesas: item.despesas,
        resultado: item.resultado,
      })),
    [dados.por_centro]
  );

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Margem por Centro/Plano</h1>
          <p className="text-gray-600">Rentabilidade por centro de custo e contribuição por plano de contas</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <DateQuickPresets
            mode="range"
            startDate={filtros.data_inicio}
            endDate={filtros.data_fim}
            onSelect={({ startDate, endDate }) =>
              setFiltros((old) => ({ ...old, data_inicio: startDate, data_fim: endDate }))
            }
            className="mb-4"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
            <div>
              <label className="form-label">Data Inicial</label>
              <input
                type="date"
                className="form-input"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros((old) => ({ ...old, data_inicio: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Data Final</label>
              <input
                type="date"
                className="form-input"
                value={filtros.data_fim}
                onChange={(e) => setFiltros((old) => ({ ...old, data_fim: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Regime</label>
              <select className="form-select" value={filtros.regime} onChange={(e) => setFiltros((old) => ({ ...old, regime: e.target.value }))}>
                <option value="competencia">Competencia</option>
                <option value="caixa">Caixa</option>
              </select>
            </div>
            <div>
              <label className="form-label">Centro de Custo</label>
              <select className="form-select" value={filtros.centro_custo_id} onChange={(e) => setFiltros((old) => ({ ...old, centro_custo_id: e.target.value }))}>
                <option value="todos">Todos</option>
                {centrosCustos.map((centro) => (
                  <option key={centro.id} value={centro.id}>
                    {centro.codigo} - {centro.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Conta Bancaria</label>
              <select className="form-select" value={filtros.conta_bancaria_id} onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))}>
                <option value="todas">Todas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.banco} - {conta.conta}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Top N</label>
              <input
                type="number"
                min="5"
                max="200"
                className="form-input"
                value={filtros.top_n}
                onChange={(e) => setFiltros((old) => ({ ...old, top_n: Number(e.target.value || 30) }))}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Receitas</p>
            <p className="text-2xl font-bold text-success-700">R$ {formatarValor(dados.resumo.receitas)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Despesas</p>
            <p className="text-2xl font-bold text-danger-700">R$ {formatarValor(dados.resumo.despesas)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Resultado</p>
            <p className={`text-2xl font-bold ${Number(dados.resumo.resultado || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              R$ {formatarValor(dados.resumo.resultado)}
            </p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Margem</p>
            <p className={`text-2xl font-bold ${Number(dados.resumo.margem_pct || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              {formatarPct(dados.resumo.margem_pct)}
            </p>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">Rentabilidade por Centro</h3>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartCentro}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="nome" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `R$ ${(Number(v) / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => [`R$ ${formatarValor(v)}`, 'Valor']} />
                <Legend />
                <Bar dataKey="receitas" name="Receitas" fill="#16a34a" />
                <Bar dataKey="despesas" name="Despesas" fill="#dc2626" />
                <Bar dataKey="resultado" name="Resultado" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Tabela por Centro de Custo</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Centro</th>
                  <th>Receitas</th>
                  <th>Despesas</th>
                  <th>Resultado</th>
                  <th>Margem %</th>
                </tr>
              </thead>
              <tbody>
                {(dados.por_centro || []).map((item) => (
                  <tr key={item.centro_custo_id}>
                    <td>{item.centro_codigo} - {item.centro_descricao}</td>
                    <td className="text-success-700">R$ {formatarValor(item.receitas)}</td>
                    <td className="text-danger-700">R$ {formatarValor(item.despesas)}</td>
                    <td className={Number(item.resultado || 0) >= 0 ? 'text-success-700 font-semibold' : 'text-danger-700 font-semibold'}>
                      R$ {formatarValor(item.resultado)}
                    </td>
                    <td className={Number(item.margem_pct || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}>{formatarPct(item.margem_pct)}</td>
                  </tr>
                ))}
                {(dados.por_centro || []).length === 0 && !carregando && (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-500 py-8">Nenhum dado encontrado para os filtros selecionados</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Contribuição por Plano de Contas</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Plano</th>
                  <th>Tipo</th>
                  <th>Valor</th>
                  <th>% no Tipo</th>
                  <th>Impacto Resultado %</th>
                </tr>
              </thead>
              <tbody>
                {(dados.por_plano || []).map((item) => (
                  <tr key={item.plano_conta_id}>
                    <td>
                      <div className="max-w-[420px]">
                        <p className="font-medium text-gray-900">{item.codigo} - {item.descricao}</p>
                        <p className="text-xs text-gray-500">{item.dre_grupo || 'Sem grupo DRE'}</p>
                      </div>
                    </td>
                    <td className={item.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'}>{item.tipo}</td>
                    <td className={item.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'}>R$ {formatarValor(item.valor)}</td>
                    <td>{formatarPct(item.participacao_tipo_pct)}</td>
                    <td className={Number(item.impacto_resultado_pct || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}>
                      {formatarPct(item.impacto_resultado_pct)}
                    </td>
                  </tr>
                ))}
                {(dados.por_plano || []).length === 0 && !carregando && (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-500 py-8">Nenhum dado encontrado para os filtros selecionados</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MargemCentroPlano;
